import React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import Slide from "@mui/material/Slide";
import { useTranslation } from "react-i18next";
import { Stack } from "@mui/system";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useState } from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setNotification } from "../../../redux/slices/notificationsSlice";
import {
  createStore,
  updateStore,
} from "../../../redux/slices/adminStoreSlice";
import { getAllProducts } from "../../../redux/slices/adminProductsSlice";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const AddEditStoreModal = ({
  open,
  onClose,
  storeToEdit,
  isEditMode,
  onSuccessAddNewStore,
  onSuccessEditStore,
  allDefinedProducts,
  allDefinedStores,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [name, setName] = useState();
  const [pib, setPib] = useState();
  const [aliases, setAliases] = useState();
  const [productsFromReceiptFields, setProductsFromReceiptFields] = useState([
    { objectId: "", name: "", aliases: "" },
  ]);

  const updateStoreLoading = useSelector(
    (state) => state.adminStores.updateStoreLoading
  );
  const updateStoreError = useSelector(
    (state) => state.adminStores.updateStoreError
  );

  const createStoreLoading = useSelector(
    (state) => state.adminStores.createStoreLoading
  );
  const createStoreError = useSelector(
    (state) => state.adminStores.createStoreError
  );
  const [samePibError, setSamePibError] = useState(false);

  const onChangeNameInput = (event) => {
    setName(event.target.value);
  };
  const onChangePibInput = (event) => {
    setPib(event.target.value);
  };

  const onSaveNewStore = async () => {
    dispatch(
      createStore({
        name,
        pib,
        product_dictionaries: aliases,
      })
    ).then(
      (response) => {
        onSuccessAddNewStore();
        dispatch(
          setNotification({
            show: true,
            message: t("notifications.successAddNewStore"),
            variant: "success",
          })
        );
      },
      (error) => {
        dispatch(
          setNotification({
            show: true,
            message: error,
            variant: "error",
          })
        );
      }
    );
  };

  const onUpdateStore = async () => {
    dispatch(
      updateStore({
        objectId: storeToEdit.objectId,
        name,
        pib,
        product_dictionaries: aliases,
      })
    ).then(
      (response) => {
        onSuccessEditStore();
        dispatch(
          setNotification({
            show: true,
            message: t("notifications.successEditStore"),
            variant: "success",
          })
        );
      },
      (error) => {
        dispatch(
          setNotification({
            show: true,
            message: error,
            variant: "error",
          })
        );
      }
    );
  };
  const isFormValid = () => {
    return name && pib && !samePibError;
  };

  const handleOnProductChange = (event, index) => {
    const products = [...productsFromReceiptFields];
    const prodValue = allDefinedProducts.find(
      (f) => f.objectId === event.target.value
    );
    products[index].name = prodValue.name;
    products[index].objectId = prodValue.objectId;
    setProductsFromReceiptFields(products);
  };
  const handleOnAliasesInputChange = (event, index) => {
    const products = [...productsFromReceiptFields];
    products[index].aliases = event.target.value;
    setProductsFromReceiptFields(products);
  };
  const onAddProductField = () => {
    const allFields = [...productsFromReceiptFields];
    allFields.push({ objectId: "", name: "", aliases: "" });
    setProductsFromReceiptFields(allFields);
  };
  const onRemoveProductField = (i) => {
    const allFields = productsFromReceiptFields.filter(
      (f, index) => i !== index
    );
    setProductsFromReceiptFields(allFields);
  };
  const processAliasesFromStore = () => {
    const prepared = [];
    const product_dictionaries = storeToEdit.product_dictionaries;
    product_dictionaries?.forEach((pd) => {
      allDefinedProducts.forEach((product) => {
        if (pd.objectId === product.objectId) {
          prepared.push({
            objectId: pd.objectId,
            name: product.name,
            aliases: pd.term,
          });
        }
      });
    });
    let preparedFinal = [];
    prepared?.forEach((f) => {
      const index = preparedFinal.findIndex((pp) => pp.objectId === f.objectId);
      if (index < 0) {
        preparedFinal.push({
          objectId: f.objectId,
          name: f.name,
          aliases: [f.aliases],
        });
      } else {
        preparedFinal?.[index]?.aliases.push(f.aliases);
      }
    });
    preparedFinal = preparedFinal?.map((f) => {
      return {
        objectId: f.objectId,
        name: f.name,
        aliases: f.aliases.join(),
        product: { objectId: f.objectId, name: f.name },
      };
    });
    setProductsFromReceiptFields(preparedFinal);
  };

  useEffect(() => {
    if (storeToEdit && isEditMode) {
      setName(storeToEdit.name);
      setPib(storeToEdit.pib);
      storeToEdit.product_dictionaries && processAliasesFromStore();
    }
  }, [storeToEdit, isEditMode]);
  useEffect(() => {
    dispatch(getAllProducts());
  }, []);

  useEffect(() => {
    if (productsFromReceiptFields) {
      const preparedDictionaire = [];
      productsFromReceiptFields.forEach((p) => {
        const termArray = p.aliases.split(",");
        termArray.forEach((t) => {
          preparedDictionaire.push({ objectId: p.objectId, term: t.trim() });
        });
      });
      setAliases(preparedDictionaire);
    }
  }, [productsFromReceiptFields]);
  useEffect(() => {
    if (allDefinedStores && pib) {
      const thereIsSamePib = allDefinedStores.find(
        (store) => store.pib === pib && storeToEdit?.pib !== pib
      );
      setSamePibError(thereIsSamePib);
    }
  }, [pib, allDefinedStores, storeToEdit]);

  function productsSelect(productReceipt, index) {
    return (
      <Stack display="flex" alignItems="end" direction="row">
        <Stack marginTop="12px" minWidth="200px">
          <InputLabel id="demo-simple-select-label">
            {t("processReceiptModal.articleWithNum", { num: index + 1 })}
          </InputLabel>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            value={productReceipt.objectId}
            onChange={(e) => handleOnProductChange(e, index)}
          >
            {allDefinedProducts?.map((product) => {
              return (
                <MenuItem value={product.objectId}>{product.name}</MenuItem>
              );
            })}
          </Select>
        </Stack>
        <Stack marginTop="35px" marginLeft="8px" maxWidth="200px">
          <TextField
            id="aliases"
            label={t("addEditStoreModal.aliases")}
            type="text"
            value={productReceipt.aliases}
            onChange={(e) => handleOnAliasesInputChange(e, index)}
            required
          ></TextField>
        </Stack>
        {index === productsFromReceiptFields.length - 1 && (
          <Stack display="flex" direction="row">
            <IconButton aria-label="add" onClick={onAddProductField}>
              <AddCircleIcon />
            </IconButton>
            {index > 0 && (
              <IconButton
                aria-label="delete"
                onClick={() => onRemoveProductField(index)}
              >
                <RemoveCircleIcon />
              </IconButton>
            )}
          </Stack>
        )}
      </Stack>
    );
  }
  function productsAdding() {
    return (
      <Stack>
        {productsFromReceiptFields.map((product, index) => {
          return productsSelect(product, index);
        })}
      </Stack>
    );
  }

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={onClose}
      aria-describedby="alert-dialog-slide-description"
    >
      <Box width="550px" padding="24px">
        <DialogTitle>
          {t(
            isEditMode
              ? "addEditStoreModal.addStoreTitle"
              : "addEditStoreModal.addStoreTitle"
          )}
        </DialogTitle>

        <Stack
          direction="column"
          display="flex"
          justifyContent="center"
          padding="0 24px"
        >
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label={t("addEditStoreModal.name")}
            type="text"
            fullWidth
            variant="standard"
            value={name || ""}
            onChange={onChangeNameInput}
            required
            error={name === ""}
            helperText={name === "" ? t("errors.requiredField") : ""}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <TextField
            autoFocus
            margin="dense"
            id="pib"
            label={t("addEditStoreModal.pib")}
            type="number"
            fullWidth
            variant="standard"
            value={pib || ""}
            onChange={onChangePibInput}
            required
            error={pib === "" || samePibError}
            helperText={
              pib === ""
                ? t("errors.requiredField")
                : samePibError
                ? t("errors.samePibError")
                : ""
            }
            InputLabelProps={{
              shrink: true,
            }}
          />

          <Stack margin="12px 0">
            <Alert severity="info">
              {t("addEditStoreModal.definingAlias")}
            </Alert>
            {productsAdding()}
          </Stack>
        </Stack>
        <DialogActions>
          <Button onClick={onClose}>{t("addEditStoreModal.cancel")}</Button>
          {isEditMode ? (
            <Button
              variant="contained"
              disabled={!isFormValid() || updateStoreLoading === "PENDING"}
              onClick={onUpdateStore}
            >
              {updateStoreLoading === "PENDING" ? (
                <CircularProgress />
              ) : (
                t("addEditStoreModal.save")
              )}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={onSaveNewStore}
              disabled={!isFormValid() || createStoreLoading === "PENDING"}
            >
              {createStoreLoading === "PENDING" ? (
                <CircularProgress />
              ) : (
                t("addEditStoreModal.save")
              )}
            </Button>
          )}
        </DialogActions>
      </Box>
    </Dialog>
  );
};
export default AddEditStoreModal;
